/**
 * Builds the `payload` half of a create-export request, one builder per report
 * kind. Each payload is the body its own report view POSTs today, so the
 * backend can reuse the view's query path unchanged.
 *
 * `scope` never reaches the wire — it only decides how the payload is composed:
 *
 * - `current_view` sends filters, search, sort and the user's KPI selection, and
 *   pages the whole result set with `offset: 0` / `limit: total_count`.
 * - `all_data` drops every narrowing field and widens the KPI set to the full
 *   catalog. Dimensions are deliberately not widened: they define row
 *   granularity, so widening them is a cross-product with no user intent behind
 *   it.
 *
 * The consequence, recorded in the contract doc: an `all_data` export and a
 * `current_view` export of an unfiltered, unsearched, unsorted view are
 * byte-identical on the wire.
 */
import type { ExportScope } from '../types';

type Payload = Record<string, unknown>;

/** Fields every paginated report adds for `current_view` and omits for `all_data`. */
const paginationFor = (scope: ExportScope, totalCount: number): Payload =>
    scope === 'current_view' ? { offset: 0, limit: totalCount } : {};

export interface CalendarExportParams {
    scope: ExportScope;
    frequency: string;
    sortBy: string;
    sortingOrder: 'asc' | 'desc';
    searchString: string;
    /** The user's visible column keys, which is what the calendar searches over. */
    searchFields: string[];
    filters: Record<string, unknown>;
    totalCount: number;
}

/**
 * `group_by` is excluded by product decision, so a grouped on-screen calendar
 * exports flat and the backend never has to emit parent/child rows.
 */
export const buildCalendarExportPayload = ({
    scope,
    frequency,
    sortBy,
    sortingOrder,
    searchString,
    searchFields,
    filters,
    totalCount,
}: CalendarExportParams): Payload => {
    if (scope === 'all_data') {
        return { frequency };
    }
    return {
        frequency,
        sort_by: sortBy,
        sorting_order: sortingOrder,
        search_string: searchString,
        search_fields: searchFields,
        filters,
        ...paginationFor(scope, totalCount),
    };
};

export interface PlanMetricExportParams {
    scope: ExportScope;
    viewType: 'dimension' | 'metric';
    /** Required whenever `viewType` is `dimension`, which is the default sub-mode. */
    dimension: string;
    kpis: string[];
    allKpiKeys: string[];
    granularity: string;
    timePeriod: string;
    /** Already merged with the attribute selection; see `mergeAttributeFilters`. */
    filters: Record<string, unknown>;
    searchString: string;
    sortBy: string;
    sortingOrder: 'asc' | 'desc';
    totalCount: number;
}

export const buildPlanMetricExportPayload = ({
    scope,
    viewType,
    dimension,
    kpis,
    allKpiKeys,
    granularity,
    timePeriod,
    filters,
    searchString,
    sortBy,
    sortingOrder,
    totalCount,
}: PlanMetricExportParams): Payload => {
    const structural: Payload = {
        view_type: viewType,
        granularity,
        time_period: timePeriod,
        ...(viewType === 'dimension' ? { dimension } : {}),
    };

    if (scope === 'all_data') {
        return { ...structural, kpis: allKpiKeys };
    }
    return {
        ...structural,
        kpis,
        filters,
        search_string: searchString,
        sort_by: sortBy,
        sorting_order: sortingOrder,
        ...paginationFor(scope, totalCount),
    };
};

export interface PromoMetricExportParams {
    scope: ExportScope;
    /** User selection, never widened for `all_data`. */
    dimensions: string[];
    kpis: string[];
    allKpiKeys: string[];
    kpiViewType: string;
    metricType: string;
    timePeriod: string;
    filters: Record<string, unknown> | null;
    searchString: string;
    sortBy: string;
    sortingOrder: 'asc' | 'desc';
    totalCount: number;
}

export const buildPromoMetricExportPayload = ({
    scope,
    dimensions,
    kpis,
    allKpiKeys,
    kpiViewType,
    metricType,
    timePeriod,
    filters,
    searchString,
    sortBy,
    sortingOrder,
    totalCount,
}: PromoMetricExportParams): Payload => {
    const structural: Payload = {
        dimensions,
        kpi_view_type: kpiViewType,
        metric_type: metricType,
        time_period: timePeriod,
    };

    if (scope === 'all_data') {
        return { ...structural, kpis: allKpiKeys };
    }
    return {
        ...structural,
        kpis,
        filters,
        search_string: searchString,
        sort_by: sortBy,
        sorting_order: sortingOrder,
        ...paginationFor(scope, totalCount),
    };
};

export interface PlanComparisonExportParams {
    /** Branches payload composition only; never emitted as a field. */
    scope: ExportScope;
    basePlanId: string;
    comparisonPlanIds: string[];
    kpiList: string[];
    allKpiKeys: string[];
    timePeriod: string;
    reportName: string;
    /** null when no KPI row is expanded; yields an empty `drilldowns`. */
    highlightedKpiKey: string | null;
    kpiLevel: string;
    metricType: string;
    searchKey: string;
}

/**
 * Unpaginated, so no `limit` / `offset` in either scope.
 *
 * `drilldowns` carries the KPI breakdown open on screen — one KPI at one
 * grouping level, chosen by clicking a row. That is inherently a current-view
 * concept, so `all_data` sends an empty list rather than a cross-product of
 * every catalog KPI at every configured level.
 */
export const buildPlanComparisonExportPayload = ({
    scope,
    basePlanId,
    comparisonPlanIds,
    kpiList,
    allKpiKeys,
    timePeriod,
    reportName,
    highlightedKpiKey,
    kpiLevel,
    metricType,
    searchKey,
}: PlanComparisonExportParams): Payload => {
    const structural: Payload = {
        base_plan_id: basePlanId,
        comparison_plan_ids: comparisonPlanIds,
        time_period: timePeriod,
        current_date: Math.floor(Date.now() / 1000),
        report_name: reportName,
    };

    if (scope === 'all_data') {
        return {
            ...structural,
            kpi_list: allKpiKeys,
            filters: {},
            drilldowns: [],
        };
    }

    const trimmedSearch = searchKey.trim();
    return {
        ...structural,
        kpi_list: kpiList,
        filters: {},
        metric_type: metricType,
        ...(trimmedSearch ? { search_key: trimmedSearch } : {}),
        drilldowns:
            highlightedKpiKey && kpiLevel
                ? [{ kpi: highlightedKpiKey, group_by: [kpiLevel] }]
                : [],
    };
};
